import React, { useCallback, useRef, useState } from "react";

// Email HTML is shown in a sandboxed frame: no scripts (no allow-scripts, and
// the policy below blocks them as well), no forms, and links open in a new tab.
// allow-same-origin only lets this page measure the frame's height; without
// allow-scripts nothing inside the frame can use it.
const SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";
const POLICY =
  "default-src 'none'; img-src data: https:; style-src 'unsafe-inline' https:; font-src data: https:";

// Parsed rather than pattern-matched, so entity tricks cannot slip through:
// <meta> (refresh redirects), <base> (link targets) and active elements are
// dropped; the sender's <style> blocks are kept for layout.
const DROP = "meta,base,script,noscript,form,iframe,frame,object,embed";
export function cleanEmailHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll(DROP).forEach((n) => n.remove());
  const styles = Array.from(doc.head.querySelectorAll("style")).map((n) => n.outerHTML).join("");
  return styles + doc.body.innerHTML;
}

const frameDoc = (html) => `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${POLICY}">
<base target="_blank">
<style>body{margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#111827;word-wrap:break-word}
img{max-width:100%;height:auto}blockquote{margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex}</style>
</head><body>${cleanEmailHtml(html)}</body></html>`;

/** One email's body: its HTML in a sandboxed frame, or its plain text. */
export default function MessageBody({ html, text }) {
  const frame = useRef(null);
  const [height, setHeight] = useState(120);

  const fit = useCallback(() => {
    const doc = frame.current?.contentDocument;
    if (doc?.body) setHeight(Math.max(60, doc.documentElement.scrollHeight + 8));
  }, []);

  if (!html) {
    return <div className="whitespace-pre-wrap break-words text-sm text-gray-800">{text}</div>;
  }
  return (
    <iframe
      ref={frame}
      title="Email message"
      sandbox={SANDBOX}
      srcDoc={frameDoc(html)}
      onLoad={() => {
        fit();
        // Images load after the frame does; fit again once they have.
        setTimeout(fit, 400);
        setTimeout(fit, 1500);
      }}
      className="w-full border-0"
      style={{ height }}
    />
  );
}
